import { useState } from "react";
import Typewriter from "typewriter-effect";
import { Sparkles } from "lucide-react";
import { BaseModal } from "./BaseModal";
import { Spinner } from "../Spinner";

const TEXT_LOADING_PHRASES = [
  "Reading through your recipe...",
  "Chopping this into ingredients...",
  "Measuring out the details...",
  "Tasting for typos...",
  "Plating up the form fields...",
];

const URL_LOADING_PHRASES = [
  "Fetching that page...",
  "Reading the recipe...",
  "Chopping this into ingredients...",
  "Measuring out the details...",
  "Plating up the form fields...",
];

export function AiMealFillModal({ onGenerate, onClose }) {
  const [mode, setMode] = useState("text");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const value = mode === "text" ? text : url;
  const canSubmit = value.trim().length > 0;

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setError(null);
  };

  const handleGenerate = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await onGenerate({ mode, value: value.trim() });
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          (mode === "text"
            ? "Couldn't generate meal details from that text. Please try again or enter it manually."
            : "Couldn't generate meal details from that link. Please try again or enter it manually."),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BaseModal
      isOpen={true}
      onClose={isLoading ? () => {} : onClose}
      title="Fill with AI"
      titleIcon={<Sparkles className="w-5 h-5" />}
      subtitle="Paste a recipe, a link, or describe the meal and we'll fill in the details for you to review"
      footer={
        !isLoading && (
          <>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerate}
              disabled={!canSubmit}
              className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              {mode === "text" ? "Generate" : "Import"}
            </button>
          </>
        )
      }
    >
      {isLoading ? (
        <div className="flex flex-col items-center justify-center gap-4 py-8">
          <Spinner size="lg" className="text-gray-900" />
          <div className="text-sm font-medium text-gray-600 min-h-[20px] text-center">
            <Typewriter
              onInit={(typewriter) => {
                typewriter.pauseFor(1800).deleteAll().start();
              }}
              options={{
                autoStart: true,
                loop: true,
                strings: mode === "text" ? TEXT_LOADING_PHRASES : URL_LOADING_PHRASES,
              }}
            />
          </div>
        </div>
      ) : (
        <>
          <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              onClick={() => switchMode("text")}
              className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                mode === "text"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Paste text
            </button>
            <button
              type="button"
              onClick={() => switchMode("url")}
              className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                mode === "url"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              From a link
            </button>
          </div>

          <div className="h-48">
            {mode === "text" ? (
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={2000}
                placeholder={`e.g. "Chicken stir fry, serves 4, about 30 minutes. 400g chicken breast, 1 red pepper, 2 tbsp soy sauce, 1 clove garlic..."`}
                className="w-full h-full resize-none px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            ) : (
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                maxLength={2048}
                placeholder="https://example.com/some-recipe"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            )}
          </div>
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
        </>
      )}
    </BaseModal>
  );
}
